import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { toast as sonnerToast } from 'sonner';
import { logger } from '@/lib/logger';

const SpotifyCallback = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const handleCallback = async () => {
      const correlationId = logger.getCorrelationId();
      logger.info('Spotify OAuth callback received', { correlationId });
      
      // ✅ FIX: Remover classe de OAuth ao processar callback
      document.body.classList.remove('spotify-oauth-active');

      let navigationTimeout: NodeJS.Timeout | null = null;

      try {
        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const error = params.get('error');

        if (error) {
          logger.error('Spotify OAuth error', new Error(error), { correlationId });
          toast({
            title: "Erro na autenticação",
            description: "Não foi possível conectar com o Spotify",
            variant: "destructive"
          });
          navigationTimeout = setTimeout(() => navigate('/profile'), 1500);
          return () => {
            if (navigationTimeout) clearTimeout(navigationTimeout);
          };
        }

        if (!code) {
          logger.error('Missing authorization code', new Error('No code in callback'), { correlationId });
          toast({
            title: "Erro",
            description: "Código de autorização não recebido",
            variant: "destructive"
          });
          navigationTimeout = setTimeout(() => navigate('/profile'), 1500);
          return () => {
            if (navigationTimeout) clearTimeout(navigationTimeout);
          };
        }

        // ✅ CORREÇÃO CRÍTICA: Usar localStorage para persistência cross-process
        const codeVerifier = localStorage.getItem('spotify_code_verifier');
        
        if (!codeVerifier) {
          logger.error('Missing code verifier', new Error('No verifier in storage'), { correlationId });
          toast({
            title: "Erro",
            description: "Sessão expirada, tente novamente",
            variant: "destructive"
          });
          navigationTimeout = setTimeout(() => navigate('/profile'), 1500);
          return () => {
            if (navigationTimeout) clearTimeout(navigationTimeout);
          };
        }

        const redirectUri = `${window.location.origin}/oauth/spotify/callback`;
        logger.info('Invoking spotify-oauth-callback', { correlationId, hasCode: true, hasVerifier: true, redirectUri });

        const { data, error: invokeError } = await supabase.functions.invoke('spotify-oauth-callback', {
          body: { code, code_verifier: codeVerifier, redirect_uri: redirectUri }
        });

        // Limpar dados do OAuth do localStorage
        localStorage.removeItem('spotify_code_verifier');
        localStorage.removeItem('spotify_state');
        localStorage.removeItem('spotify_redirect_uri');
        localStorage.removeItem('spotify_platform');
        localStorage.removeItem('spotify_auth_method');
        localStorage.removeItem('spotify_auth_attempt');

        if (invokeError) {
          logger.error('Edge function error', invokeError, { correlationId });
          
          // Detectar erro de redirect_uri_mismatch
          const errorMsg = invokeError.message || '';
          const isRedirectError = errorMsg.includes('redirect_uri') || errorMsg.includes('mismatch');
          
          if (isRedirectError) {
            // Toast persistente com ação para documentação
            sonnerToast.error('🚫 Redirect URI incorreto', {
              description: 'Os Redirect URIs não estão configurados corretamente no Spotify Dashboard. Adicione todos os 3 URIs necessários.',
              duration: Infinity, // Não desaparece automaticamente
              action: {
                label: 'Ver solução',
                onClick: () => window.open('https://github.com/yourusername/youbetterrun/blob/main/SPOTIFY_OAUTH_DIAGNOSTIC.md', '_blank')
              }
            });
          } else {
            // Send to frontend error log
            await supabase.functions.invoke('log-frontend-error', {
              body: {
                error_type: 'oauth_callback_error',
                error_message: invokeError.message,
                stack_trace: invokeError.stack || '',
                user_agent: navigator.userAgent,
                url: window.location.href,
                correlation_id: correlationId,
                metadata: { provider: 'spotify', step: 'edge_function_invoke' }
              }
            });

            toast({
              title: "Erro ao conectar",
              description: "Não foi possível completar a conexão com o Spotify",
              variant: "destructive"
            });
          }
          
          navigationTimeout = setTimeout(() => navigate('/profile'), 1500);
          return;
        }

        logger.info('Spotify connected successfully', { correlationId, responseCorrelationId: data?.correlationId });

        toast({
          title: "Spotify conectado!",
          description: "Sua conta foi conectada com sucesso"
        });
        
        // ✅ Disparar evento para useSpotifyAuth atualizar estado
        window.dispatchEvent(new CustomEvent('spotifyAuthSuccess'));

        navigate('/profile');

      } catch (error) {
        const err = error instanceof Error ? error : new Error(String(error));
        logger.error('Unexpected error in Spotify callback', err, { correlationId });
        
        await supabase.functions.invoke('log-frontend-error', {
          body: {
            error_type: 'oauth_callback_exception',
            error_message: err.message,
            stack_trace: err.stack || '',
            user_agent: navigator.userAgent,
            url: window.location.href,
            correlation_id: correlationId,
            metadata: { provider: 'spotify', step: 'callback_handler' }
          }
        });

        toast({
          title: "Erro inesperado",
          description: "Ocorreu um erro ao processar o callback",
          variant: "destructive"
        });
        
        navigationTimeout = setTimeout(() => navigate('/profile'), 1500);
      }
      
      // Cleanup function
      return () => {
        if (navigationTimeout) {
          clearTimeout(navigationTimeout);
        }
      };
    };

    const cleanup = handleCallback();
    return () => {
      cleanup.then(cleanupFn => cleanupFn?.());
    };
  }, [navigate, toast]);

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Conectando com Spotify...</p>
      </div>
    </div>
  );
};

export default SpotifyCallback;
